import { Button } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Clock,
  AlertTriangle,
  Bot,
  Users,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, RealtimeMetrics, Conversation } from '../lib/admin-api';
import { StatsCard } from '../components/admin/StatsCard';

export function DashboardPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<RealtimeMetrics | null>(null);
  const [recentConversations, setRecentConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      if (!profile?.organization_id) return;

      setLoading(true);
      const [metricsData, conversationsData] = await Promise.all([
        adminApi.getDashboardMetrics(profile.organization_id),
        adminApi.getRecentConversations(profile.organization_id, 5),
      ]);

      setMetrics(metricsData);
      setRecentConversations(conversationsData);
      setLoading(false);
    }

    fetchData();
  }, [profile?.organization_id]);

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      open: 'bg-success text-success-foreground',
      pending: 'bg-warning text-warning-foreground',
      resolved: 'bg-info text-info-foreground',
      closed: 'bg-muted text-foreground',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.open}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div>
        <h1 className="support-page-title">Overview</h1>
        <p className="text-muted-foreground mt-1">Your conversations, response times, and team workload.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Open Conversations"
          value={metrics?.open_conversations ?? 0}
          icon={MessageSquare}
          tone="primary"
        />
        <StatsCard
          title="Pending"
          value={metrics?.pending_conversations ?? 0}
          icon={Clock}
          tone="warning"
        />
        <StatsCard
          title="Unassigned"
          value={metrics?.unassigned_count ?? 0}
          icon={Users}
          tone="info"
        />
        <StatsCard
          title="Urgent"
          value={metrics?.urgent_count ?? 0}
          icon={AlertTriangle}
          tone="danger"
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard
          title="AI Resolved (24h)"
          value={metrics?.ai_resolved_24h ?? 0}
          icon={Bot}
          tone="success"
        />
        <StatsCard
          title="Total (24h)"
          value={metrics?.total_24h ?? 0}
          icon={CheckCircle}
          tone="primary"
        />
        <StatsCard
          title="Avg First Response"
          value={formatDuration(metrics?.avg_first_response_24h ?? null)}
          icon={Clock}
          tone="info"
        />
      </div>

      {/* SLA Breached Alert */}
      {(metrics?.sla_breached_count ?? 0) > 0 && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-error-text" />
          <div>
            <p className="text-sm font-medium text-error-text">
              {metrics?.sla_breached_count} conversation{metrics?.sla_breached_count === 1 ? '' : 's'} with SLA breach
            </p>
            <p className="text-sm text-error-text">Immediate attention required</p>
          </div>
        </div>
      )}

      {/* Recent Conversations */}
      <div className="bg-card rounded-lg border border-border">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Recent conversations</h2>
        </div>
        <div className="divide-y divide-border">
          {recentConversations.length === 0 ? (
            <div className="px-6 py-8 text-center text-muted-foreground">
              No conversations yet
            </div>
          ) : (
            recentConversations.map((conversation) => (
              <Link
                key={conversation.id}
                to={`/conversations/${conversation.id}`}
                className="block px-6 py-4 hover:bg-muted cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {conversation.title || `Conversation #${conversation.id.slice(0, 8)}`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {conversation.channel} · {new Date(conversation.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {conversation.ai_handled && (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-info text-info-foreground rounded-full">
                        <Bot className="w-3 h-3 mr-1" />
                        AI
                      </span>
                    )}
                    {getStatusBadge(conversation.status)}
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
        {recentConversations.length > 0 && (
          <div className="px-6 py-3 border-t border-border">
            <Button variant="ghost"
              onClick={() => navigate('/conversations')}
              className="text-sm text-link hover:text-link font-medium"
            >
              View all conversations →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
